/**
 * Data layer for `braze-event-sync-integration`.
 * Thin typed fetch wrappers over /api/v1/braze-event-sync-integration.
 * Raw `fetch()` is confined to this file per the dash contract.
 *
 * Backend (see apps/api/src/routes/braze-event-sync-integration.ts):
 *   POST /api/v1/braze-event-sync-integration                    -> enqueue event
 *   GET  /api/v1/braze-event-sync-integration?status=pending|synced|failed
 *   POST /api/v1/braze-event-sync-integration/:event_id/status   -> mark synced|failed
 */

export type BrazeSyncStatus = "pending" | "synced" | "failed";

export interface BrazeEventRow {
	id: number;
	tenant_id: string;
	event_id: string;
	external_id: string | null;
	event_name: string;
	properties_json: string;
	sync_status: BrazeSyncStatus;
	synced_at: number | null;
	created_at: number;
}

export interface ListBrazeEventsResponse {
	items: BrazeEventRow[];
}

export interface EnqueueBrazeEventBody {
	event_id: string;
	event_name: string;
	external_id?: string | null;
	properties?: Record<string, unknown>;
}

export interface EnqueueBrazeEventResponse {
	ok: boolean;
	event_id: string;
	status: BrazeSyncStatus;
	created_at: number;
}

export interface MarkBrazeStatusBody {
	status: "synced" | "failed";
}

export interface MarkBrazeStatusResponse {
	ok: boolean;
	event_id: string;
	status: BrazeSyncStatus;
	synced_at: number;
}

const BASE = "/api/v1/braze-event-sync-integration";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(status?: BrazeSyncStatus): Promise<ListBrazeEventsResponse> {
	const url = status ? `${BASE}?status=${encodeURIComponent(status)}` : BASE;
	const res = await fetch(url, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListBrazeEventsResponse>(res);
}

export async function enqueueEvent(body: EnqueueBrazeEventBody): Promise<EnqueueBrazeEventResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<EnqueueBrazeEventResponse>(res);
}

export async function markStatus(eventId: string, body: MarkBrazeStatusBody): Promise<MarkBrazeStatusResponse> {
	const res = await fetch(`${BASE}/${encodeURIComponent(eventId)}/status`, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<MarkBrazeStatusResponse>(res);
}

export const brazeEventSyncIntegrationQueryKeys = {
	all: ["braze-event-sync-integration"] as const,
	list: (status?: BrazeSyncStatus) =>
		status
			? (["braze-event-sync-integration", "list", status] as const)
			: (["braze-event-sync-integration", "list"] as const),
	detail: (id: string) => ["braze-event-sync-integration", id] as const,
};
